/**
 * broker-branding.ts — the CANONICAL broker-branding shape (Wave W4 — branding).
 *
 * A Mandant's customer-facing surfaces (transactional customer emails + the customer
 * portal) carry the BROKER's own brand, not Brokly's. The broker branding lives in the
 * portal_config.branding JSONB blob alongside the existing logoRef.
 *
 * Three broker-settable colours (all validated #RRGGBB hex, all optional/nullable):
 *   - primaryColor   — buttons / links / active states / accents (the CTA fill)
 *   - secondaryColor — secondary buttons / badges / highlights (accent trim)
 *   - headerColor    — the coloured band behind the logo (email header + portal top bar)
 *
 * FIXED for readability (NEVER broker-settable): body text (dark ink), content/card
 * background (white/light), borders. A broker's brand can never make an email or the
 * portal unreadable.
 *
 * These three keys are ADDITIVE to the branding blob. The legacy PortalBrandingDto
 * (logoRef + primaryHex/secondaryHex — the admin/customer-portal PUT surface) is left
 * untouched; W4 introduces this dedicated triplet as the canonical set the broker-branded
 * email + portal theme read.
 *
 * [Source: docs/design/2026-07-29_mandant-config-catalog.md → "Build plan + specs" → W4]
 */

/** The hex-colour pattern every broker colour is validated against: #RRGGBB. */
export const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * True iff `value` is a valid #RRGGBB hex-colour string.
 * Used to guard broker-supplied colours before they reach an email/portal theme:
 * a bad value degrades to null (the Brokly default), never a broken style.
 */
export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && HEX_COLOR_PATTERN.test(value);
}

/** The three broker-settable colour keys, in canonical order. */
export const BROKER_BRANDING_COLOR_KEYS = [
  'primaryColor',
  'secondaryColor',
  'headerColor',
] as const;

/** Union of the broker colour keys. */
export type BrokerBrandingColorKey = (typeof BROKER_BRANDING_COLOR_KEYS)[number];

/**
 * BrokerBrandingColors — the three broker-settable colours.
 * Each is a #RRGGBB hex string or null (unset → falls back to the Brokly default).
 */
export interface BrokerBrandingColors {
  primaryColor: string | null;
  secondaryColor: string | null;
  headerColor: string | null;
}

/**
 * BrokerBrandingDto — the canonical broker-branding shape stored in the branding blob.
 * logoRef: a string reference (object-store URL / key) — NO blob stored in the DB.
 */
export interface BrokerBrandingDto extends BrokerBrandingColors {
  logoRef: string | null;
}

/**
 * Read the three broker colours out of an arbitrary branding blob, keeping ONLY
 * valid #RRGGBB values (invalid / missing → null). Pure — safe to call on untrusted
 * JSONB read from portal_config.branding.
 */
export function readBrokerBrandingColors(branding: unknown): BrokerBrandingColors {
  const blob = (branding ?? {}) as Record<string, unknown>;
  const pick = (key: BrokerBrandingColorKey): string | null =>
    isHexColor(blob[key]) ? (blob[key] as string) : null;
  return {
    primaryColor: pick('primaryColor'),
    secondaryColor: pick('secondaryColor'),
    headerColor: pick('headerColor'),
  };
}
